import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { Business, BUSINESSES } from '../data';
import { Reveal } from './Reveal';
function BusinessCard({
  business
}: {
  business: Business;
}) {
  const isComingSoon = business.status === 'coming-soon';
  const suitabilityLabel = isComingSoon ? 'Kommer erbjuda' : 'Perfekt för';
  return <article className={`group/card overflow-hidden rounded-sm border transition-all duration-700 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brun/10 ${isComingSoon ? 'border-terrakotta/35 bg-sand' : 'border-brun/10 bg-beige'}`} data-id="element-20" data-mp="1kk6rn9qgn0d">
      <div className="relative overflow-hidden" data-id="element-21" data-mp="bbetesqw5omc">
        <img src={business.cover} alt={business.place ? `${business.name} i ${business.place}` : business.name} className={`h-72 w-full object-cover transition-transform duration-[1200ms] hover:scale-105 md:h-96 ${isComingSoon ? 'saturate-[0.75]' : ''}`} data-id="element-22" data-mp="6jwsg4ovbo1d" />
        <div className={`absolute inset-0 bg-gradient-to-t ${isComingSoon ? 'from-skog/80 via-skog/15' : 'from-brun/55 to-transparent'}`} data-id="element-23" data-mp="39yspm3mgdzfc" />
        {isComingSoon && <div className="absolute right-5 top-5 rounded-full bg-smorgul px-4 py-2 text-[10px] font-medium uppercase tracking-wide2 text-brun md:right-7 md:top-7" data-id="element-24" data-mp="2083j37z05t5g">
            Kommer snart
          </div>}
        <div className="absolute bottom-0 left-0 p-7 md:p-9" data-id="element-25" data-mp="2a1kbq8nel27j">
          {business.place && <p className="text-[11px] uppercase tracking-wide2 text-warm/70" data-id="element-26" data-mp="1cdk2tq3yl1qc">{business.place}</p>}
          <h3 className="mt-1 font-display text-3xl font-light text-warm md:text-4xl" data-id="element-27" data-mp="1hcwow8n0a4rv">
            {business.name}
          </h3>
        </div>
      </div>

      <div className="p-7 md:p-9" data-id="element-28" data-mp="1qcjn6lkfuugp">
        <p className="text-[11px] uppercase leading-relaxed tracking-wide2 text-terrakotta" data-id="element-29" data-mp="1q9dyh7fq1uli">
          {business.services}
        </p>
        <div className="mt-6 max-w-2xl space-y-4 text-base leading-relaxed text-brun-soft" data-id="element-30" data-mp="31qdqs322k9fj">
          {business.description.map(paragraph => <p key={paragraph} data-id="element-31" data-mp="12kcc1sngqwpb">{paragraph}</p>)}
        </div>

        <div className="mt-9 border-t border-brun/10 pt-7" data-id="element-32" data-mp="xnlz3e6ygh84">
          <p className="text-[11px] uppercase tracking-wide2 text-skog" data-id="element-33" data-mp="1bmlkmcz68g02">{suitabilityLabel}</p>
          <ul className="mt-4 flex flex-wrap gap-2" aria-label={suitabilityLabel} data-id="element-34" data-mp="1bk7r48k53bum">
            {business.perfectFor.map(occasion => <li key={occasion} className="rounded-full border border-brun/15 px-3 py-1.5 text-xs text-brun-soft" data-id="element-35" data-mp="ix4yfx259vca">
                {occasion}
              </li>)}
          </ul>
        </div>

        <div className="mt-9 grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-36" data-mp="sorfia15yomy">
          {business.products && business.products.map(item => <figure key={item.id} className="group" data-id="element-37" data-mp="nuwmuh3kyzbq">
              <div className="overflow-hidden rounded-sm" data-id="element-38" data-mp="6n4xq9hmvpv">
                <img src={item.image} alt={item.category} className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-39" data-mp="52by52tnku4" />
              </div>
              <figcaption className="mt-2 text-[11px] uppercase tracking-wide2 text-brun-soft" data-id="element-40" data-mp="2bnficx2o6x2d">
                {item.category}
              </figcaption>
            </figure>)}
        </div>

        {!isComingSoon && <div className="mt-10 border-t border-brun/10 pt-8" data-id="element-41" data-mp="22rzk83uvtvqz">
            <Link to={`/partner/${business.slug}`} className="group inline-flex items-center gap-3 rounded-full bg-skog px-7 py-3.5 text-xs uppercase tracking-wide2 text-warm transition-colors hover:bg-brun" data-id="element-42" data-mp="2qkpw6d2scf19">
              Se sortiment
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" data-id="element-43" data-mp="2lk23lmywrq4x" />
            </Link>
          </div>}
      </div>
    </article>;
}
export function Businesses() {
  return <section id="verksamheter" className="w-full bg-warm py-28 md:py-40" data-id="element-44" data-mp="1a6g5hcb2me79">
      <div className="mx-auto max-w-7xl px-6 md:px-10" data-id="element-45" data-mp="1petel4vsb91e">
        <Reveal className="max-w-3xl" data-id="element-46" data-mp="1pgcuuvhfxk36">
          <p className="mb-6 text-[12px] uppercase tracking-wide3 text-terrakotta" data-id="element-47" data-mp="1kolax3ssicm0">Våra verksamheter</p>
          <h2 className="font-display text-4xl font-light leading-tight text-brun md:text-5xl" data-id="element-48" data-mp="1gm87wn5fgssx">
            Vi väljer inte ut flest – vi väljer ut bäst.
          </h2>
          <p className="mt-7 max-w-2xl text-lg leading-relaxed text-brun-soft" data-id="element-49" data-mp="1blk9vhis7bp7">
            Smaken som Baken samlar verksamheter som vi själva tror på. Gemensamt för dem alla är ett kompromisslöst fokus på smak, kvalitet och hantverk.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-10 md:mt-20 lg:grid-cols-2" data-id="element-50" data-mp="1zbxysz62kufk">
          {BUSINESSES.map((business, index) => <Reveal key={business.name} delay={index * 0.08} className={index === 0 ? 'lg:col-span-2' : ''} data-id="element-51" data-mp="1udwkssni3lay" data-mpi={index}>
              <BusinessCard business={business} data-id="element-52" data-mp="nvzpcu6zc6dt" />
            </Reveal>)}
        </div>

        <Reveal className="mx-auto mt-24 max-w-3xl text-center md:mt-32" data-id="element-53" data-mp="j12hvrpcm531">
          <p className="text-[12px] uppercase tracking-wide3 text-terrakotta" data-id="element-54" data-mp="8wm1mx7yilyp">Fler smaker väntar</p>
          <h3 className="mt-6 font-display text-3xl font-light leading-tight text-brun md:text-4xl" data-id="element-55" data-mp="dunircltob7j">
            Smaken som Baken börjar med fyra verksamheter som vi känner utan och innan.
          </h3>
          <p className="mt-7 text-lg leading-relaxed text-brun-soft" data-id="element-56" data-mp="nli5ei2zdcti">
            De delar samma filosofi, samma kvalitetskrav och samma passion för riktigt bra mat. I takt med att plattformen växer kommer vi att välkomna fler handplockade producenter, bagerier, restauranger, caféer, kockar och dryckesaktörer från hela Småland.
          </p>
          <p className="mt-8 font-display text-xl italic leading-relaxed text-bourgogne md:text-2xl" data-id="element-57" data-mp="16ijeadznnx2q">
            Inte för att bli störst. Utan för att fortsätta vara den plats där du alltid hittar det vi själva hade valt att servera våra egna gäster.
          </p>
        </Reveal>
      </div>
    </section>;
}